/**
 * matchmaking.ts — the PURE matchmaking batch logic + wire types, shared by the
 * Cloudflare matchmaker Durable Object (workers/matchmaker.ts) and the game
 * client (Flow / MatchmakingClient). Framework-free and time-injected so the
 * whole "find racers" behaviour is unit-testable off the network.
 *
 * OWNER-LOCKED rules this encodes (2026-06-17 + 2026-07-02):
 *   - A matchmade race is a 6-racer field with UP TO 4 humans; AI fills the
 *     rest and is presented EXACTLY like a found human — never revealed.
 *   - The search window is ~10 s: anyone searching inside the same window is
 *     pooled; when it closes, the match starts with whoever was found.
 *   - Quick Match is zero-choice (the matchmaker picks the city); Start a Tour
 *     is a 5-stop tour (the room rolls the cities). M142: a searcher may
 *     instead be routed into an OPEN club race of its mode — Quick Match into
 *     a club's single race, Start a Tour into a club tour of ANY length
 *     (3 / 5 / 9 stops, owner decision 2026-10-02).
 *
 * The "theater": while searching, the on-screen racer count fills 1/6 → 6/6.
 * Real humans appear the moment they join; the remaining slots "join" at staged
 * RANDOM times across the window (the AI backfill, indistinguishable by design
 * — see revealTimes). The displayed count is a HIGH-WATER MARK — it never goes
 * down, even if a real human replaces a staged join.
 */

/** One matchmade field: 6 racers, at most 4 of them human (owner-locked). */
export const MM_FIELD = 6;
export const MM_MAX_HUMANS = 4;
/** How long a search batch stays open for more humans. */
export const MM_SEARCH_WINDOW_MS = 10_000;
/** A matchmade tour is 5 stops; the ROOM rolls which 5 cities, fresh per tour
 *  (M87 — the matchmaker sends only the length, no picks). */
export const MM_TOUR_LEN = 5;
/** Matchmade lobby fallback (M63): how long the room lets its lobby sit before
 *  forcing the grid. The lobby is a real pick-your-ride screen now, so this is
 *  generous — but bounded, so one AFK matched player can never hold three
 *  strangers hostage. (Friend rooms have NO timer — explicit START + all-ready.) */
export const MM_ROOM_LOBBY_MS = 25_000;

export type MmMode = 'quick' | 'tour';

/** Server → client: the live "racers found" count while searching. */
export interface MmQueueMsg {
  t: 'queue';
  found: number;
  total: number;
}

/** Server → client: matched — connect to this referee room. Every member gets
 *  the SAME config so whichever client reaches the room first configures it
 *  identically (the room reads config from its first caller's URL). */
export interface MmMatchMsg {
  t: 'match';
  room: string;
  field: number;
  /** How many humans were matched — the room starts once they all connect. */
  mmcap: number;
  /** Quick match: the city to race (matchmaker-picked). */
  city?: string;
  /** Tour: the number of cities (overrides city). */
  tour?: number;
  /** V2 Phase B (M107): the ROOM-HOST key for this match (config/network.ts
   *  ROOM_HOSTS) — the matchmaker is continent-sharded, so it hands every
   *  matched player the same nearby regional server. Optional: an older
   *  matchmaker simply doesn't send it (client falls back to its default). */
  h?: string;
}

export type MmServerMsg = MmQueueMsg | MmMatchMsg;

/** V2 Phase B (M107): continent code (request.cf.continent) → room-host key.
 *  Three regions cover the map: Americas → us (iad), Europe+Africa → eu (ams),
 *  Asia+Oceania → asia (sin). Unknown ('XX', tor nodes, etc.) → us. PURE so
 *  the mapping is pinned by tests — it must agree with ROOM_HOSTS keys. */
export function hostKeyForContinent(continent: string): string {
  switch (continent) {
    case 'EU':
    case 'AF':
      return 'eu';
    case 'AS':
    case 'OC':
      return 'asia';
    default:
      return 'us'; // NA, SA, AN, XX
  }
}

/** The staged joins land inside this slice of the search window: the front
 *  ~third stays visibly "waiting" (real humans own it — an instant 6/6 would
 *  look canned), and everything fires before the window closes at 100%. */
export const MM_REVEAL_MIN_FRAC = 0.35;
export const MM_REVEAL_MAX_FRAC = 0.95;
/** Two staged joins never land closer together than this. Truly independent
 *  arrivals CAN collide, but on screen a same-tick double-join reads as one
 *  glitchy jump of +2 — a tiny spacer keeps every join its own visible beat. */
export const MM_REVEAL_MIN_GAP_MS = 120;

/** Staged "join" times for the AI backfill — RANDOM, so the fill mimics real
 *  people trickling in (owner note 2026-07-02: the old even spacing was
 *  robotically metronomic). We sample field-1 INDEPENDENT uniform times in
 *  [35%, 95%] of the window and sort them ascending: sorted independent
 *  uniforms ARE the statistics of real random arrivals — natural clumps and
 *  gaps included, and every search looks different. A ~120 ms minimum gap is
 *  then enforced (forward pass pushes collisions apart, a backward pass reins
 *  the tail back under 95% — the band is huge relative to the gaps, so
 *  everything stays inside it). field-1 times cover the worst case (a lone
 *  searcher needs every other slot staged); extras are simply never shown once
 *  real humans hold those slots.
 *
 *  PURE given `rand` (inject a seeded rand in tests for determinism); the
 *  Math.random default keeps the worker call site zero-config. */
export function revealTimes(
  windowMs: number,
  field: number,
  rand: () => number = Math.random,
): number[] {
  const first = windowMs * MM_REVEAL_MIN_FRAC;
  const last = windowMs * MM_REVEAL_MAX_FRAC;
  const n = field - 1;
  const times: number[] = [];
  for (let i = 0; i < n; i++) times.push(first + (last - first) * rand());
  times.sort((a, b) => a - b);
  // Effective gap degrades gracefully if someone ever shrinks the window so far
  // that n spacers wouldn't fit the band (never true at the shipped 10 s).
  const gap = n > 1 ? Math.min(MM_REVEAL_MIN_GAP_MS, (last - first) / (n - 1)) : 0;
  // Forward: push any too-close neighbour later...
  for (let i = 1; i < n; i++) {
    if (times[i] - times[i - 1] < gap) times[i] = times[i - 1] + gap;
  }
  // ...backward: if that shoved the tail past 95%, pull it (and only what must
  // follow) back so the last join still beats the window close.
  for (let i = n - 1; i >= 0; i--) {
    const cap = i === n - 1 ? last : times[i + 1] - gap;
    if (times[i] > cap) times[i] = cap;
  }
  return times;
}

/**
 * One open search batch. Time never read internally — the caller (the DO's
 * timers, or a test) drives joins/leaves/reveals and asks what to display.
 */
export class MatchBatch<TMember> {
  private readonly members = new Set<TMember>();
  /** Staged AI joins currently shown (clamped so humans + staged ≤ field). */
  private staged = 0;
  /** High-water mark of the displayed count — the count never decreases. */
  private shown = 0;

  constructor(
    readonly mode: MmMode,
    private readonly field = MM_FIELD,
    private readonly maxHumans = MM_MAX_HUMANS,
  ) {}

  /** A real searcher joined. Returns false if the batch has no human room left
   *  (it matched, or maxHumans are already here — the caller opens a new batch). */
  add(member: TMember): boolean {
    if (this.members.size >= this.maxHumans) return false;
    this.members.add(member);
    // A real human takes priority over a staged join: displace one if the field
    // is visually full. The shown high-water mark keeps the count monotonic.
    this.staged = Math.min(this.staged, this.field - this.members.size);
    return true;
  }

  /** A searcher cancelled / disconnected before the match formed. */
  remove(member: TMember): void {
    this.members.delete(member);
  }

  /** A staged (AI backfill) join fires — one more "racer" appears. */
  reveal(): void {
    this.staged = Math.min(this.staged + 1, this.field - this.members.size);
  }

  /** The count to display: humans + staged joins, monotonically non-decreasing. */
  found(): number {
    this.shown = Math.max(this.shown, Math.min(this.field, this.members.size + this.staged));
    return this.shown;
  }

  humans(): number {
    return this.members.size;
  }

  isEmpty(): boolean {
    return this.members.size === 0;
  }

  /** Full of real humans — match NOW, no need to wait out the window. */
  isFull(): boolean {
    return this.members.size >= this.maxHumans;
  }

  /** The matched roster (order = slot order: the first to search is slot 0). */
  roster(): TMember[] {
    return [...this.members];
  }
}

// ---- M140: public crew rooms ("anchors") ----------------------------------------

/** The room-host keys a crew anchor may name — MUST equal config/network.ts
 *  ROOM_HOSTS keys (a test pins it; this module also runs in the worker,
 *  which cannot import the Vite-env network config). */
export const ANCHOR_HOST_KEYS = ['us', 'eu', 'asia', 'cf'] as const;
const ANCHOR_TTL_MIN_MS = 3_000;
const ANCHOR_TTL_MAX_MS = 20_000;
/** At most this many crew rooms wait in one matchmaker instance. */
export const ANCHOR_MAX = 32;

/**
 * A PUBLIC crew room's open seats, registered with the matchmaker by the
 * crew member who pressed FIND RACERS (`?mm=quick|tour&anchor=ROOM&race=UUID&
 * seats=&ttl=`). While it lives, searchers of the SAME mode in this region
 * are sent straight into that room instead of a fresh matchmade one — real
 * strangers race the crew. Quick Match fills a single race, Start a Tour
 * fills a club tour (M142). Seats left when it expires are staged as AI by
 * the room itself.
 *
 * M140b (review): an anchor is only accepted for a GENUINE open, public crew
 * race — the matchmaker asks the database (crew_anchor_check) with the race
 * uuid, which only crew members receive, and takes the room's host, city and
 * field from the database's answer, never from the request. Without that, any
 * script could point a continent's Quick Match at rooms of its choosing.
 */
export interface AnchorSpec {
  room: string;
  /** The crew race this anchor belongs to (for logs). */
  race: string;
  /** Room-host key the crew room lives on. */
  h: string;
  /** Open seats still on offer. */
  seats: number;
  field: number;
  /** A single race's city ('' for a tour — the room rolls the route). */
  city: string;
  /** M142: 0 = a single race in `city`; 3-9 = a club TOUR of that many stops. */
  tour: number;
  expiresAt: number;
}

/** What a crew anchor request asks for — FORMAT only; the race itself is
 *  verified against the database before anything is routed. */
export interface AnchorRequest {
  race: string;
  room: string;
  seats: number;
  ttlMs: number;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Validate an anchor request's shape (every param is user-controlled). */
export function parseAnchorRequest(params: URLSearchParams): AnchorRequest | null {
  const race = (params.get('race') ?? '').toLowerCase();
  const room = params.get('anchor') ?? '';
  const seats = Number(params.get('seats'));
  // A missing ttl must read as "use the default", not as 0 (Number(null) = 0).
  const ttlRaw = params.get('ttl');
  const ttl = ttlRaw === null || ttlRaw.trim() === '' ? NaN : Number(ttlRaw);
  if (!UUID_RE.test(race)) return null;
  if (!/^[A-Z0-9]{4,8}$/.test(room)) return null;
  if (!Number.isInteger(seats) || seats < 1 || seats > 6) return null;
  const ttlMs = Number.isFinite(ttl)
    ? Math.min(ANCHOR_TTL_MAX_MS, Math.max(ANCHOR_TTL_MIN_MS, Math.round(ttl)))
    : 10_000;
  return { race, room, seats, ttlMs };
}

/** The database's answer (public.crew_anchor_check). Every field is read as
 *  untrusted JSON and validated by anchorSpecFrom. */
export interface AnchorCheck {
  ok?: unknown;
  host?: unknown;
  city?: unknown;
  field?: unknown;
  /** M142: the club race's crew_races.tour_len (0 = single race, 3-9 = tour).
   *  Absent (an older database function) reads as a single race. */
  tour_len?: unknown;
}

/** M142: a club race's tour length off the wire — 3-9 stops, anything else is
 *  a single race (0). Mirrors the crew_races.tour_len check and crewLogic's
 *  sanitizeCrewTourLen (this module also runs in the worker, so it keeps its
 *  own copy; a test pins the two together). */
export function sanitizeAnchorTour(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 3 && n <= 9 ? n : 0;
}

/** M142: which searchers a club race wants — a tour is filled by Start a Tour
 *  searchers, a single race by Quick Match. The client opens its anchor on
 *  that matchmaker, and the matchmaker refuses an anchor whose VERIFIED race
 *  disagrees with the mode it arrived on. */
export function anchorModeFor(tour: number): MmMode {
  return tour > 0 ? 'tour' : 'quick';
}

/** LOCAL TESTING ONLY (the worker's ANCHOR_VERIFY=off): take the room config
 *  from the request itself, as if the database had confirmed it. */
export function devAnchorCheck(params: URLSearchParams): AnchorCheck {
  return {
    ok: true,
    host: params.get('h') ?? '',
    city: params.get('city') ?? '',
    field: Number(params.get('field')),
    tour_len: params.get('tour') ?? 0,
  };
}

/** The routing spec for a request the database confirmed, arriving on the
 *  `mode` matchmaker. null = refuse (not confirmed, junk, or the wrong mode:
 *  a club tour on Quick Match or a single race on Start a Tour). */
export function anchorSpecFrom(
  req: AnchorRequest,
  check: AnchorCheck | null,
  now: number,
  validCity: (id: string) => boolean,
  mode: MmMode,
): AnchorSpec | null {
  if (!check || check.ok !== true) return null;
  const h = typeof check.host === 'string' ? check.host : '';
  const city = typeof check.city === 'string' ? check.city : '';
  const field = Number(check.field);
  const tour = sanitizeAnchorTour(check.tour_len);
  if (anchorModeFor(tour) !== mode) return null;
  if (!(ANCHOR_HOST_KEYS as readonly string[]).includes(h)) return null;
  if (!Number.isInteger(field) || field < 2 || field > 7) return null;
  // A single race needs its city; a tour's route is rolled by the room.
  if (tour === 0 && !validCity(city)) return null;
  const seats = Math.min(req.seats, field - 1);
  if (seats < 1) return null;
  return {
    room: req.room,
    race: req.race,
    h,
    seats,
    field,
    city: tour > 0 ? '' : city,
    tour,
    expiresAt: now + req.ttlMs,
  };
}

/** The live anchors of one matchmaker instance. Time-injected + pure. */
export class AnchorBook<TSock> {
  private readonly items: { sock: TSock; spec: AnchorSpec }[] = [];

  /** false = refused (full book, or the room is already anchored). */
  add(sock: TSock, spec: AnchorSpec): boolean {
    if (this.items.length >= ANCHOR_MAX) return false;
    if (this.items.some((a) => a.spec.room === spec.room)) return false;
    this.items.push({ sock, spec });
    return true;
  }

  remove(sock: TSock): void {
    const i = this.items.findIndex((a) => a.sock === sock);
    if (i >= 0) this.items.splice(i, 1);
  }

  /** The crew room's open seats changed (a crewmate joined or left during
   *  the search): offer exactly that many from now on (0 = stop routing). */
  setSeats(sock: TSock, seats: number): void {
    const a = this.items.find((x) => x.sock === sock);
    if (!a || !Number.isFinite(seats)) return;
    a.spec.seats = Math.max(0, Math.min(a.spec.field - 1, Math.floor(seats)));
  }

  /** Drop expired anchors; returns their sockets (the DO closes them). */
  expire(now: number): TSock[] {
    const out: TSock[] = [];
    for (let i = this.items.length - 1; i >= 0; i--) {
      if (this.items[i].spec.expiresAt <= now) {
        out.push(this.items[i].sock);
        this.items.splice(i, 1);
      }
    }
    return out;
  }

  /** Take one seat from the OLDEST live anchor of the searcher's mode (first
   *  come, first filled): Quick Match fills club single races, Start a Tour
   *  fills club tours of any length (M142). */
  claim(now: number, mode: MmMode): { sock: TSock; spec: AnchorSpec } | null {
    for (const a of this.items) {
      if (a.spec.expiresAt > now && a.spec.seats > 0 && anchorModeFor(a.spec.tour) === mode) {
        a.spec.seats--;
        return { sock: a.sock, spec: { ...a.spec } };
      }
    }
    return null;
  }

  get size(): number {
    return this.items.length;
  }
}

/** The match message a routed searcher gets: connect to the crew room. A
 *  tour carries its length (the searcher's join URL gets `&tour=`, and the
 *  lobby reads "N-stop tour"); a single race carries its city. The room is
 *  already configured by the crew, so this only has to agree with it. */
export function anchorMatchMsg(spec: AnchorSpec): MmMatchMsg {
  const msg: MmMatchMsg = { t: 'match', room: spec.room, field: spec.field, mmcap: 1, h: spec.h };
  if (spec.tour > 0) msg.tour = spec.tour;
  else msg.city = spec.city;
  return msg;
}
